import { toast } from "../toast/toast";
import {
  ReagentCategory,
  ReagentGroup,
  ReagentSize,
  Unit,
} from "../../../backend/src/types";
import { ENDPOINT } from "../endpoint/endpoint";
import { errorMessage } from "../function/errorMessage";

type Response<T> = {
  data: T;
  error?: any;
};

export type GroupResponse = {
  group: ReagentGroup;
  sizes: ReagentSize[];
  error?: any;
};

interface GetReagentGroupListPayload {
  tenant_id: number;
  lab_id: number;
}

export async function getReagentGroupList(
  payload: GetReagentGroupListPayload,
): Promise<ReagentGroup[]> {
  try {
    const res = await fetch(ENDPOINT.reagentGroup.getReagentGroupList, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<ReagentGroup[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    const reagentGroups = [...data].sort((a, b) =>
      a.name.localeCompare(b.name, "ja"),
    );
    return reagentGroups;
  } catch (error) {
    errorMessage("試薬マスターリストの取得に失敗しました", error);
    return [];
  }
}

interface AddReagentGroupPayload {
  reagentGroup: ReagentGroup;
  reagentSizes: ReagentSize[];
}

export async function addReagentGroup(
  payload: AddReagentGroupPayload,
): Promise<GroupResponse | null> {
  try {
    const res = await fetch(ENDPOINT.reagentGroup.addReagentGroup, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { group, sizes, error }: GroupResponse = await res.json();

    if (!res.ok) {
      if (res.status === 409) {
        toast.error("すでに同じ名称の試薬マスターが登録されています。");
        return null;
      }
      throw new Error(error ?? "Server Error");
    }

    return { group, sizes };
  } catch (error) {
    if (error instanceof Error) {
      const dbError = error as any;
      if (dbError.code === 23503) {
        errorMessage("その名称はすでに登録されています。", error);
      }
    }
    errorMessage("試薬マスターの追加に失敗しました。", error);
    return null;
  }
}

interface EditReagentGroupPayload {
  reagentGroup: ReagentGroup;
  reagentSizes: ReagentSize[];
  isSameName: boolean;
}

export async function editReagentGroup(
  payload: EditReagentGroupPayload,
): Promise<GroupResponse | null> {
  try {
    const res = await fetch(ENDPOINT.reagentGroup.editReagentGroup, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { group, sizes, error }: GroupResponse = await res.json();

    if (!res.ok) {
      if (res.status === 409) {
        toast.error("すでに同じ名称の試薬マスターが登録されています。");
        return null;
      }
      throw new Error(error ?? "Server Error");
    }

    return { group, sizes };
  } catch (error) {
    errorMessage("試薬マスターの編集に失敗しました。", error);
    return null;
  }
}

interface DeleteReagentGroupPayload {
  group_id: number;
}

export async function deleteReagentGroup(payload: DeleteReagentGroupPayload) {
  try {
    const res = await fetch(ENDPOINT.reagentGroup.deleteReagentGroup, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.status === 204) {
      return;
    }

    if (!res.ok) {
      const { error }: Response<null> = await res.json();
      throw new Error(error ?? "Server Error");
    }
  } catch (error) {
    errorMessage("試薬マスターの削除に失敗しました。", error);
  }
}

interface CheckReagentGroupUsedPayload {
  group_id: number;
  tenant_id: number;
}

export async function checkReagentGroupUsed(
  payload: CheckReagentGroupUsedPayload,
): Promise<number> {
  try {
    const res = await fetch(ENDPOINT.reagentGroup.checkReagentGroupUsed, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<number> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage(
      "試薬マスター使用数の取得に失敗しました。削除を実行することができません。",
      error,
    );
    return -1;
  }
}

export async function getReagentCategoryList(): Promise<ReagentCategory[]> {
  try {
    const res = await fetch(ENDPOINT.reagentSize.getReagentCategoryList, {
      method: "GET",
    });

    const { data, error }: Response<ReagentCategory[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("試薬カテゴリリストの取得に失敗しました。", error);
    return [];
  }
}

export async function getUnitList(): Promise<Unit[]> {
  try {
    const res = await fetch(ENDPOINT.reagentSize.getUnitList, {
      method: "GET",
    });

    const { data, error }: Response<Unit[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("単位リストの取得に失敗しました。", error);
    return [];
  }
}

interface GetUnitListPayload {
  group_id: number;
}

export async function getReagentSizeList(
  payload: GetUnitListPayload,
): Promise<ReagentSize[]> {
  try {
    const res = await fetch(ENDPOINT.reagentSize.getReagentSizeList, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: Response<ReagentSize[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("試薬容量リストの取得に失敗しました。", error);
    return [];
  }
}
